import Icon1 from "@assets/icons/Icon";
import BaseModal from "@components/modals/BaseModal";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import Button from "@components/ui/buttons/Button";
import DropDown from "@components/ui/dropdown/DropDown";
import DropDownSearch from "@components/ui/dropdown/DropDownSearch";
import TextField from "@components/ui/textfields/TextField";
import AreaTextField from "@components/ui/textfields/AreaTextField";
import DatePicker from "@components/ui/selection/DatePicker";
import { useState, useMemo, useCallback, useEffect } from "react";
import ConfirmationModal from "@components/support/ConfirmationModal";
import { useVehiculesStore } from "@store/vehiculesStore";
import { useDataStore } from "@store/user_org";
import { isOwnerOrAdmin } from "@utils/permissions";
import { navigateTo } from "context/navigation.service";
import TagPicker from "@components/ui/tags/TagPicker";
import { bookingsApi } from "@services/api/bookings";
import { vehiclesApi } from "@services/api/vehicles";
import type {
  Vehicle,
  VehicleClass,
  VehicleLocationCategory,
  VehicleStatus,
  VehicleFuelType,
  VehicleColoris,
  VehicleReservation,
} from "types/garage/vehicules";
import { VEHICLE_LOCATION_CATEGORIES } from "types/garage/vehicules";
import VehicleImage from "@components/ui/VehicleImage";
import { isMecFullDate, mecPickerLabel } from "@utils/vehicleMec";

const COLORIS_OPTIONS = [
  { value: "noir", label: "Noir" },
  { value: "blanc", label: "Blanc" },
  { value: "gris", label: "Gris" },
  { value: "bleu", label: "Bleu" },
  { value: "rouge", label: "Rouge" },
  { value: "vert", label: "Vert" },
  { value: "jaune", label: "Jaune" },
  { value: "marron", label: "Marron" },
  { value: "orange", label: "Orange" },
  { value: "autre", label: "Autre" },
];

const MARQUE_OPTIONS = [
  { value: "ABARTH", label: "ABARTH" },
  { value: "ALFA ROMEO", label: "ALFA ROMEO" },
  { value: "AUDI", label: "AUDI" },
  { value: "BMW", label: "BMW" },
  { value: "CITROEN", label: "CITROËN" },
  { value: "CUPRA", label: "CUPRA" },
  { value: "DACIA", label: "DACIA" },
  { value: "DS", label: "DS" },
  { value: "FIAT", label: "FIAT" },
  { value: "FORD", label: "FORD" },
  { value: "HONDA", label: "HONDA" },
  { value: "HYUNDAI", label: "HYUNDAI" },
  { value: "IVECO", label: "IVECO" },
  { value: "JAGUAR", label: "JAGUAR" },
  { value: "JEEP", label: "JEEP" },
  { value: "KIA", label: "KIA" },
  { value: "LAND ROVER", label: "LAND ROVER" },
  { value: "LEXUS", label: "LEXUS" },
  { value: "MAN", label: "MAN" },
  { value: "MAZDA", label: "MAZDA" },
  { value: "MERCEDES", label: "MERCEDES" },
  { value: "MINI", label: "MINI" },
  { value: "MITSUBISHI", label: "MITSUBISHI" },
  { value: "NISSAN", label: "NISSAN" },
  { value: "OPEL", label: "OPEL" },
  { value: "PEUGEOT", label: "PEUGEOT" },
  { value: "PORSCHE", label: "PORSCHE" },
  { value: "RENAULT", label: "RENAULT" },
  { value: "SEAT", label: "SEAT" },
  { value: "SKODA", label: "SKODA" },
  { value: "SMART", label: "SMART" },
  { value: "SSANGYONG", label: "SSANGYONG" },
  { value: "SUBARU", label: "SUBARU" },
  { value: "SUZUKI", label: "SUZUKI" },
  { value: "TESLA", label: "TESLA" },
  { value: "TOYOTA", label: "TOYOTA" },
  { value: "VOLKSWAGEN", label: "VOLKSWAGEN" },
  { value: "VOLVO", label: "VOLVO" },
];

const TYPE_OPTIONS = [
  { value: "pret", label: "Véhicule de prêt" },
  { value: "location", label: "Véhicule de location" },
  { value: "velos", label: "Vélo" },
];

const FUEL_OPTIONS = [
  { value: "diesel", label: "Diesel" },
  { value: "essence", label: "Essence" },
  { value: "electrique", label: "Electrique" },
  { value: "gpl", label: "GPL" },
  { value: "hybride", label: "Hybride" },
  { value: "autre", label: "Autre" },
];

// Rental categories, parsed from the vehicle name ("X LOC - CAT B - C3" → "B")
// into loc_category by the fleet scraper. "6" kept for a couple of legacy rows.
const LOC_CATEGORY_OPTIONS = VEHICLE_LOCATION_CATEGORIES.map((c) => ({ value: c, label: c }));

const CLASS_OPTIONS = [
  { value: "vp", label: "VP (Véhicule particulier)" },
  { value: "vu", label: "VU (Véhicule utilitaire)" },
];

const STATUS_OPTIONS = [
  { value: "disponible", label: "Disponible" },
  { value: "en_cours", label: "En cours d'utilisation" },
  { value: "hors_service", label: "Hors service" },
  { value: "reserve", label: "Réservé" },
];


const EMPTY_VEHICLE: Vehicle = {
  id: "",
  name: "",
  plate: "",
  km: 0,
  category: "autre",
  usage: "pret",
  fuel_type: "diesel",
  designation: "",
  tags: [],
  modele: "",
  status: "disponible",
  has_print: false,
};

function formatDateFr(dateStr: string): string {
  const [y, m, d] = dateStr.split("-");
  return `${d}/${m}/${y}`;
}

function formatHour(h: number): string {
  const hours = Math.floor(h);
  const minutes = Math.round((h - hours) * 60);
  return `${String(hours).padStart(2, "0")}h${minutes > 0 ? String(minutes).padStart(2, "0") : "00"}`;
}

function formatReservationTime(r: VehicleReservation): string {
  const isSingleDay = r.startDate === r.endDate;
  const start = formatDateFr(r.startDate);
  const end = formatDateFr(r.endDate);

  if (isSingleDay && r.startHour != null && r.durationHours != null) {
    return `${start} · ${formatHour(r.startHour)} – ${formatHour(r.startHour + r.durationHours)}`;
  }

  const startTime = r.startHour != null ? ` à ${formatHour(r.startHour)}` : "";
  const endTime = r.returnHour != null ? ` à ${formatHour(r.returnHour)}` : "";

  return `${start}${startTime} → ${end}${endTime}`;
}

function VehicleReservationsSection({ vehicleId }: { vehicleId: string }) {
  const reservations = useVehiculesStore((s) => s.reservations);

  const today = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  }, []);

  const vehicleRes = useMemo(
    () => reservations.filter((r) => r.vehicleId === vehicleId),
    [reservations, vehicleId],
  );

  const currentRes = useMemo(
    () => vehicleRes.find((r) => r.startDate <= today && r.endDate >= today),
    [vehicleRes, today],
  );

  const upcomingRes = useMemo(
    () =>
      vehicleRes
        .filter((r) => r.startDate > today)
        .sort((a, b) => a.startDate.localeCompare(b.startDate)),
    [vehicleRes, today],
  );

  const handleGoToReservation = (r: VehicleReservation) => {
    navigateTo({ route: `/vehicules?reservation=${r.id}` });
  };

  if (!currentRes && upcomingRes.length === 0) {
    return (
      <div className="px-4 pb-2">
        <div className="flex flex-row items-center gap-2 p-3 bg-neutral-50 rounded-lg border border-neutral-200">
          <Icon1 name="calendar" size={16} color="#A1A1A1" />
          <span className="text-h3 text-neutral-400">Aucune réservation à venir</span>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 pb-2 flex flex-col gap-2">
      <span className="text-h3 font-medium text-neutral-500">Réservations</span>

      {currentRes && (
        <div
          className="flex flex-row items-center gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200 cursor-pointer hover:bg-blue-100 transition-colors"
          onClick={() => handleGoToReservation(currentRes)}
        >
          <div className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />
          <div className="flex flex-col flex-1 min-w-0">
            <span className="text-h3 font-medium text-blue-700">En prêt — {currentRes.clientName}</span>
            <span className="text-h4 text-blue-500">
              {formatReservationTime(currentRes)}
            </span>
          </div>
          <Icon1 name="chevron-right" size={16} color="#3B82F6" />
        </div>
      )}

      {upcomingRes.slice(0, 3).map((r) => (
        <div
          key={r.id}
          className="flex flex-row items-center gap-3 p-3 bg-neutral-50 rounded-lg border border-neutral-200 cursor-pointer hover:bg-neutral-100 transition-colors"
          onClick={() => handleGoToReservation(r)}
        >
          <div className="w-2 h-2 rounded-full bg-purple-400 flex-shrink-0" />
          <div className="flex flex-col flex-1 min-w-0">
            <span className="text-h3 font-medium text-neutral-700">{r.clientName}</span>
            <span className="text-h4 text-neutral-400">
              {formatReservationTime(r)}
            </span>
          </div>
          <Icon1 name="chevron-right" size={16} color="#A1A1A1" />
        </div>
      ))}

      {upcomingRes.length > 3 && (
        <span className="text-h4 text-neutral-400 text-center">
          + {upcomingRes.length - 3} autre{upcomingRes.length - 3 > 1 ? "s" : ""}
        </span>
      )}
    </div>
  );
}

interface VehiculeModalProps {
  vehicle?: Vehicle;
  onClose: () => void;
  onSave: (vehicle: Vehicle) => void;
  onDelete?: (vehicleId: string) => void;
}

export default function VehiculeModal({ vehicle, onClose, onSave, onDelete }: VehiculeModalProps) {
  const isCreate = !vehicle;
  const [form, setForm] = useState<Vehicle>({ ...(vehicle || EMPTY_VEHICLE) });
  const [lookingUp, setLookingUp] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const orgData = useDataStore((s) => s.orgData);
  const userData = useDataStore((s) => s.userData);
  const canDelete = isOwnerOrAdmin(userData);

  const hasVehicleInfo = !!(form.marque && form.modele);
  const [dbModels, setDbModels] = useState<{ id: string; marque: string; modele: string }[]>([]);

  // Fetch vehicle models from DB on mount
  useEffect(() => {
    if (!orgData?.id) return;
    vehiclesApi.listModels(orgData.id).then(setDbModels).catch(() => {});
  }, [orgData?.id]);

  const modeleOptions = useMemo(() => {
    const marque = (form.marque || "").toUpperCase();
    if (!marque) return [];
    const models = dbModels
      .filter((m) => m.marque === marque)
      .map((m) => ({ value: m.modele, label: m.modele }));
    // Deduplicate
    const seen = new Set<string>();
    return models.filter((m) => {
      if (seen.has(m.value)) return false;
      seen.add(m.value);
      return true;
    });
  }, [form.marque, dbModels]);

  // Auto-save new model to DB when marque+modele are set (from lookup or manual)
  const ensureModelExists = useCallback(
    async (marque: string, modele: string) => {
      if (!orgData?.id || !marque || !modele) return;
      const exists = dbModels.some(
        (m) => m.marque === marque.toUpperCase() && m.modele === modele.toUpperCase(),
      );
      if (!exists) {
        try {
          const created = await vehiclesApi.createModel(orgData.id, marque, modele);
          setDbModels((prev) => [...prev, created]);
        } catch {
          // ignore — not critical
        }
      }
    },
    [orgData?.id, dbModels],
  );

  const update = <K extends keyof Vehicle>(key: K, value: Vehicle[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handlePlateLookup = useCallback(async (plate: string) => {
    if (!orgData?.id || plate.length < 5) return;
    setLookingUp(true);
    try {
      const result = await bookingsApi.lookupVehicle(plate, orgData.id);
      if (result.found) {
        const marque = result.marque || "";
        const modele = result.modele || "";
        setForm((prev) => ({
          ...prev,
          marque: marque || prev.marque,
          modele: modele || prev.modele,
          vin: (result as any).vin || prev.vin,
          coloris: (result as any).coloris || prev.coloris,
          fuel_type: (result as any).fuel_type || prev.fuel_type,
          km: result.km || prev.km,
          // MEC au jour près seulement : une année seule (anciennes fiches) n'est pas une date.
          mec: isMecFullDate(result.mec) ? result.mec : prev.mec,
          image_url: result.image_url || prev.image_url,
        }));
        if (marque && modele) {
          ensureModelExists(marque, modele);
        }
      }
    } catch {
      // Lookup failed — user can fill manually
    }
    setLookingUp(false);
  }, [orgData?.id]);

  const handleSave = () => {
    onSave(form);
    onClose();
  };

  return (
    <BaseModal
      title={isCreate ? "Nouveau véhicule" : "Modifier un Véhicule"}
      startIcon={<Icon1 name={isCreate ? "plus" : "modify"} size={20} />}
      onClose={onClose}
      width="max-w-2xl"
    >
      <div className="flex flex-col gap-3 p-4">
        {/* Immatriculation lookup (first for create mode) */}
        {isCreate && (
          <div className="flex flex-row gap-2 items-end">
            <div className="flex-1">
              <TextField
                label="Immatriculation"
                type="plate"
                value={form.plate}
                onChange={(v) => update("plate", v)}
                placeholder="AA-123-BB"
              />
            </div>
            <Button
              variant="small"
              label={lookingUp ? "Recherche..." : "Rechercher"}
              onClick={() => handlePlateLookup(form.plate)}
              disabled={lookingUp || form.plate.length < 5}
            />
          </div>
        )}

        {/* Image area */}
        <div
          className="w-full h-200 rounded-xl border border-neutral-200 bg-neutral-50 flex items-center justify-center overflow-hidden"
        >
          {hasVehicleInfo ? (
            <VehicleImage marque={form.marque} modele={form.modele} coloris={form.coloris} mec={form.mec} alt={form.name} className="w-full h-full" />
          ) : (
            <div className="flex flex-col items-center gap-2 text-neutral-400">
              <Icon1 name="image" size={40} color="#D4D4D4" />
              <span className="text-h3 font-medium">
                {isCreate ? "Entrez l'immatriculation pour pré-remplir" : "Sélectionnez une marque et un modèle"}
              </span>
            </div>
          )}
        </div>

        {/* Nom / Coloris */}
        <div className="flex flex-row gap-2">
          <div className="flex-1">
            <TextField
              label="Nom"
              value={form.name}
              onChange={(v) => update("name", v)}
            />
          </div>
          <div className="flex-1">
            <DropDown
              label="Coloris"
              options={COLORIS_OPTIONS}
              value={form.coloris || ""}
              onSelect={(v) => update("coloris", v as VehicleColoris)}
            />
          </div>
        </div>

        {/* Marque / MEC */}
        <div className="flex flex-row gap-2">
          <div className="flex-1">
            <DropDownSearch
              label="Marque"
              options={MARQUE_OPTIONS}
              value={form.marque || ""}
              onSelect={(v) => {
                update("marque", v as string);
                if (v !== form.marque) update("modele", "");
              }}
            />
          </div>
          <div className="flex-1">
            <DatePicker
              label={mecPickerLabel(form.mec)}
              seconds={isMecFullDate(form.mec) ? form.mec : null}
              onChange={(s) => update("mec", s)}
            />
          </div>
        </div>

        {/* Modèle / VIN */}
        <div className="flex flex-row gap-2">
          <div className="flex-1">
            <DropDownSearch
              label="Modèle"
              options={modeleOptions}
              value={form.modele || ""}
              onSelect={(v) => {
                update("modele", v as string);
                if (v && form.marque) ensureModelExists(form.marque, v as string);
              }}
              placeholder={form.marque ? "Rechercher un modèle..." : "Sélectionnez une marque d'abord"}
              disabled={!form.marque}
            />
          </div>
          <div className="flex-1">
            <TextField
              label="VIN"
              value={form.vin || ""}
              onChange={(v) => update("vin", v)}
            />
          </div>
        </div>

        {/* Immatriculation (edit mode) / Km */}
        <div className="flex flex-row gap-2">
          {!isCreate && (
            <div className="flex-1">
              <TextField
                label="Immatriculation"
                type="plate"
                value={form.plate}
                onChange={(v) => update("plate", v)}
              />
            </div>
          )}
          <div className="flex-1">
            <TextField
              label="Km"
              type="number"
              value={String(form.km)}
              onChange={(v) => update("km", Number(v) || 0)}
            />
          </div>
        </div>

        {/* Type / Status */}
        <div className="flex flex-row gap-2">
          <div className="flex-1">
            <DropDown
              label="Type"
              options={TYPE_OPTIONS}
              value={form.usage}
              onSelect={(v) => update("usage", v as Vehicle["usage"])}
            />
          </div>
          <div className="flex-1">
            <DropDown
              label="Status"
              options={STATUS_OPTIONS}
              value={form.status}
              onSelect={(v) => update("status", v as VehicleStatus)}
            />
          </div>
        </div>

        {/* Fuel type / Category */}
        <div className="flex flex-row gap-2">
          <div className="flex-1">
            <DropDown
              label="Carburant"
              options={FUEL_OPTIONS}
              value={form.fuel_type}
              onSelect={(v) => update("fuel_type", v as VehicleFuelType)}
            />
          </div>
          <div className="flex-1">
            <DropDown
              label="Catégorie"
              options={LOC_CATEGORY_OPTIONS}
              value={form.loc_category || ""}
              onSelect={(v) => update("loc_category", (v || undefined) as VehicleLocationCategory | undefined)}
            />
          </div>
        </div>

        {/* Vehicle class (VP/VU) */}
        <DropDown
          label="Classe"
          options={CLASS_OPTIONS}
          value={form.vehicle_class || ""}
          onSelect={(v) => update("vehicle_class", (v || undefined) as VehicleClass | undefined)}
        />

        {/* Notes */}
        <AreaTextField
          label="Notes"
          value={form.notes || ""}
          onChange={(v) => update("notes", v)}
          rows={3}
        />

        {/* Tags */}
        <TagPicker
          scope="vehicle"
          value={form.tag_ids || []}
          onSelect={(ids) => update("tag_ids", ids)}
        />
      </div>

      {/* Reservations */}
      {!isCreate && <VehicleReservationsSection vehicleId={form.id} />}

      {/* Footer */}
      <div className="border-t-1 p-4 flex flex-row items-center justify-between">
        {!isCreate && onDelete && canDelete ? (
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="text-h3 text-red-400 hover:text-red-600 transition-colors"
          >
            Supprimer
          </button>
        ) : <div />}
        <div className="flex flex-row gap-2">
          <SecondaryButton variant="small" onClick={onClose}>
            Fermer
          </SecondaryButton>
          <Button
            variant="small"
            label={isCreate ? "Créer" : "Enregistrer"}
            onClick={handleSave}
          />
        </div>
      </div>

      {showDeleteConfirm && vehicle && (
        <ConfirmationModal
          title="Supprimer ce véhicule ?"
          message={`Le véhicule ${vehicle.plate || vehicle.name} sera définitivement supprimé.`}
          onClose={() => setShowDeleteConfirm(false)}
          onConfirm={() => {
            onDelete?.(vehicle.id);
            onClose();
          }}
          confirmLabel="Supprimer"
        />
      )}
    </BaseModal>
  );
}
